/**
 * Participant views of the modules that have more than one version (#685).
 * A family route (/impactpath, /pivotpoints) and the wall views show the
 * version the instance's chain uses; ModuleRoute picks it from here.
 */
import lazyWithReload from '../utils/lazyWithReload';
import { moduleIdForFamily } from '../utils/participantEntry';

export const PARTICIPANT_VIEWS = {
  'impact_path.v1': lazyWithReload(() => import('../screens/ImpactPathView')),
  'impact_path.v2': lazyWithReload(() => import('../impactmap/ImpactMapView')),
  'pivot_points.v1': lazyWithReload(() => import('../screens/PivotPointsScreen')),
  'pivot_points.v2': lazyWithReload(() => import('../pivotpoints/PivotPointsV2Screen')),
  'pivot_points.v3': lazyWithReload(() => import('../modules/pivot_points_v3/PivotsV3Screen')),
  'solutions.v1': lazyWithReload(() => import('../screens/SolutionsScreen')),
  // Ratkaisut ja mielipiteet (#1180). solutions.v2 is not here: it has its own route, /ratkaisut-v2
  'solutions.v3': lazyWithReload(() => import('../modules/solutions_v3/SolutionsV3Screen')),
  // Ratkaisut v4 on Ongelmakartta v3's vipupisteet (#1474)
  'solutions.v4': lazyWithReload(() => import('../modules/solutions_v4/RatkaisutV4Screen')),
  // Ongelmat: v1 the dialogue, v2 problems on a map (#1326)
  'ongelmat.v1': lazyWithReload(() => import('../screens/systemic-v2/OngelmaScreen')),
  'ongelmat.v2': lazyWithReload(() => import('../modules/ongelmat_v2/OngelmatV2Screen')),
  // Muutoksen esteet: v1 the stepper and dialogue, v2 a map of one's own problems and a lens matrix (#1364)
  'ankkurit.v1': lazyWithReload(() => import('../screens/systemic-v2/SystemicV2Screen')),
  'ankkurit.v2': lazyWithReload(() => import('../modules/ankkurit_v2/AnkkuritV2Screen')),
  // Juurisyyt: v1 the swipe decks with refinement, v2 a list and a focus deck per obstacle (#1374)
  'juurisyyt.v1': lazyWithReload(() => import('../screens/systemic-v2/JuurisyyGate')),
  'juurisyyt.v2': lazyWithReload(() => import('../modules/juurisyyt_v2/JuurisyytV2Screen')),
  // Ongelmakartta: v2 the whole map in one view, v3 the vipupisteet on the perimeter map (#1389)
  'systemic_map.v2': lazyWithReload(() => import('../screens/systemic-v2/SummaryMapScreen')),
  'systemic_map.v3': lazyWithReload(() => import('../modules/systemic_map_v3/OngelmakarttaV3Screen')),
  // Tulevaisuustestaus: v1 on Ratkaisut v2's metric projections, v2 on Ratkaisut v4's moves per vipupiste (#1474)
  'future_reimagined.v1': lazyWithReload(() => import('../screens/future-reimagined/FutureReimaginedScreen')),
  'future_reimagined.v2': lazyWithReload(() => import('../modules/future_reimagined_v2/FutureV2Screen')),
};

/** Shown when the chain does not include the family (a direct link from an older workshop). */
export const DEFAULT_MODULE = {
  impact_path: 'impact_path.v1',
  pivot_points: 'pivot_points.v1',
  solutions: 'solutions.v1',
  ongelmat: 'ongelmat.v1',
  ankkurit: 'ankkurit.v1',
  juurisyyt: 'juurisyyt.v1',
  // v1 has its own route (/systeemikartta)
  systemic_map: 'systemic_map.v2',
  future_reimagined: 'future_reimagined.v1',
};

/**
 * The module a family route shows: the chain's version of the family, or the
 * default when the chain has none or its version has no view here (solutions.v2
 * has its own route, so /ratkaisut in its chains shows v1 as before).
 */
export function viewModuleFor(chain, family) {
  const chosen = moduleIdForFamily(chain, family);
  return PARTICIPANT_VIEWS[chosen] ? chosen : DEFAULT_MODULE[family];
}
